import { Material } from '@/models/Materials';

export interface MaterialAreaScore {
	bioScore: number;
	area: number;
	weightedScore: number;
}

export interface RoomMaterialsInput {
	wallMaterial: Material;
	floorMaterial: Material;
	ceilingMaterial: Material;
	wallArea: number;
	floorArea: number;
	ceilingArea: number;
}

export interface MaterialScoreDetails {
	score: number;
	totalSurfaceArea: number;
	recommendation: string;
	breakdown: {
		wall: MaterialAreaScore;
		floor: MaterialAreaScore;
		ceiling: MaterialAreaScore;
	};
}

export interface MaterialIdsInput {
	wallMaterialId?: string;
	floorMaterialId?: string;
	ceilingMaterialId?: string;
	wallArea: number;
	floorArea: number;
	ceilingArea: number;
}

export function getMaterialRecommendationForScore(overallScore: number): string {
	const score = Math.round(overallScore);
	if (score <= 3)
		return 'The current materials offer limited biophilic benefit. Increasing the share of natural or low-VOC options would noticeably improve the score.';
	if (score <= 4)
		return 'The material selection shows some natural elements, but consistency could be improved by reducing synthetic content.';
	if (score <= 5) return 'Incorporate more natural materials like wood or stone.';

	if (score <= 7)
		return 'Material selection is generally well-balanced with acceptable environmental characteristics.';
	if (score <= 8)
		return 'Strong emphasis on natural or low-impact materials contributes positively to environmental quality.';

	return 'Excellent material selection with outstanding biophilic and environmental performance.';
}

// Main function for calculating the material score of a room
// Formula: ((Wall Score × Wall Area) + (Floor Score × Floor Area) + (Ceiling Score × Ceiling Area)) / Total Surface Area
export function calculateMaterialScore(input: RoomMaterialsInput): MaterialScoreDetails {
	const wallContribution = input.wallMaterial.bioScore * input.wallArea;
	const floorContribution = input.floorMaterial.bioScore * input.floorArea;
	const ceilingContribution = input.ceilingMaterial.bioScore * input.ceilingArea;

	const totalSurfaceArea = input.wallArea + input.floorArea + input.ceilingArea;

	const totalScore = wallContribution + floorContribution + ceilingContribution;
	const materialsScore =
		totalSurfaceArea === 0 ? 0 : parseFloat((totalScore / totalSurfaceArea).toFixed(2));

	return {
		score: materialsScore,
		totalSurfaceArea,
		recommendation: getMaterialRecommendationForScore(materialsScore),
		breakdown: {
			wall: {
				bioScore: input.wallMaterial.bioScore,
				area: input.wallArea,
				weightedScore: parseFloat(wallContribution.toFixed(2)),
			},
			floor: {
				bioScore: input.floorMaterial.bioScore,
				area: input.floorArea,
				weightedScore: parseFloat(floorContribution.toFixed(2)),
			},
			ceiling: {
				bioScore: input.ceilingMaterial.bioScore,
				area: input.ceilingArea,
				weightedScore: parseFloat(ceilingContribution.toFixed(2)),
			},
		},
	};
}

export function calculateMaterialScoreFromIds(
	input: MaterialIdsInput,
	resolveMaterialById: (id: string) => Material | undefined
): MaterialScoreDetails | undefined {
	if (!input.wallMaterialId || !input.floorMaterialId || !input.ceilingMaterialId) {
		return undefined;
	}

	const wallMaterial = resolveMaterialById(input.wallMaterialId);
	const floorMaterial = resolveMaterialById(input.floorMaterialId);
	const ceilingMaterial = resolveMaterialById(input.ceilingMaterialId);

	if (!wallMaterial || !floorMaterial || !ceilingMaterial) {
		return undefined;
	}

	return calculateMaterialScore({
		wallMaterial,
		floorMaterial,
		ceilingMaterial,
		wallArea: input.wallArea,
		floorArea: input.floorArea,
		ceilingArea: input.ceilingArea,
	});
}
